import { useBunja } from 'bunja/react';
import { useAtom, useAtomValue } from 'jotai';
import { useTranslations } from 'next-intl';

import { LeverageBadge, OrderSideBadge } from '@flipster/features/src/misc/badge';
import { SymbolDisplay } from '@flipster/features/src/misc/display';
import { MovableModal } from '@flipster/features/src/shared/movable-modal';
import { T } from '@flipster/i18n';
import { getPositionDisplayPrice } from '@flipster/r230522/original/ticker-price';
import { createSegmentedControl } from '@flipster/react/design-system/segmented-control';
import { Tooltip } from '@flipster/react/design-system/tooltip';
import Formatted from '@flipster/react/design-utils/formatted';
import { css } from '@flipster/styled-system/css';
import { divider, hstack, stack } from '@flipster/styled-system/patterns';
import { getQuoteCurrencyPrecision } from '@flipster/utils/constants/currency';
import Num from '@flipster/utils/num';

import { addPositionModalBunja } from '../state';
import LimitInputSection from './LimitInputSection';
import ValueInputSection from './ValueInputSection';

const SegmentedControl = createSegmentedControl<'market' | 'limit'>();

const InputStep = () => {
  const t = useTranslations();
  const {
    positionAtom,
    tabAtom,
    instrumentAtom,
    estTotalValueAtom,
    estLiquidationPriceAtom,
    targetUnitAtom,
    targetPrecisionAtom,
    inputModeAtom,
    targetPriceAtom,
  } = useBunja(addPositionModalBunja);
  const position = useAtomValue(positionAtom);
  const instrument = useAtomValue(instrumentAtom);
  const [tab, setTab] = useAtom(tabAtom);
  const estTotalValue = useAtomValue(estTotalValueAtom);
  const estLiquidationPrice = useAtomValue(estLiquidationPriceAtom);
  const targetUnit = useAtomValue(targetUnitAtom);
  const targetPrecision = useAtomValue(targetPrecisionAtom);
  const inputMode = useAtomValue(inputModeAtom);
  const targetPrice = useAtomValue(targetPriceAtom);

  return (
    <>
      <MovableModal.Header>
        <MovableModal.Title label={t('Position_add_position.r250401.Add_position_title')} />
      </MovableModal.Header>
      <MovableModal.Content>
        <SegmentedControl
          value={tab}
          defaultValue='market'
          size='medium'
          css={{ width: '100%', mb: 24 }}
          onChange={(value) => {
            setTab(value);
          }}
        >
          <SegmentedControl.Item value='market'>
            <div className={css({ textStyle: 'Text/M/Strong' })}>{t('Trade.r251008.Market')}</div>
          </SegmentedControl.Item>
          <SegmentedControl.Item value='limit'>
            <div className={css({ textStyle: 'Text/M/Strong' })}>{t('Trade.r251104.Limit')}</div>
          </SegmentedControl.Item>
        </SegmentedControl>
        <div className={css({ mt: 4, mb: 12 })}>
          <SymbolDisplay
            symbol={position.symbol}
            icon={<SymbolDisplay.Icon size={32} space={12} />}
            mainLabel={
              <div className={hstack({ gap: 8 })}>
                <SymbolDisplay.Label
                  value='shortName'
                  css={{ textStyle: 'Text/L/Strong', color: 'content.default.level-5' }}
                />
                <div className={hstack({ gap: 4 })}>
                  <OrderSideBadge orderSide={position.side} />
                  <LeverageBadge leverage={position.initLeverage} marginType={position.marginType} />
                </div>
              </div>
            }
            subLabel={
              <Formatted.Number
                css={{ textStyle: 'Text/L/Monospace/Regular', color: 'content.default.level-2' }}
                value={getPositionDisplayPrice(position)}
                precision={instrument.pricePrecision}
                toFixed
                method='round-half-up'
              />
            }
          />
        </div>
        <div className={stack({ gap: 12 })}>
          {tab === 'limit' && <LimitInputSection />}
          <ValueInputSection />
        </div>
        <div className={divider({ orientation: 'horizontal', color: 'border.default.level-2', my: 16 })} />
        <dl
          className={stack({
            gap: 4,
            '& > div': {
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              gap: 8,
            },
            '& dt': {
              textStyle: 'Text/L/Regular',
              color: 'content.default.level-2',
            },
            '& dd': {
              textStyle: 'Text/L/Monospace/Regular',
              color: 'content.default.level-5',
              textAlign: 'right',
            },
          })}
        >
          <div>
            <dt>{t('Position.r250401.Est_total_value')}</dt>
            <dd>
              <Formatted.Number
                value={estTotalValue}
                precision={targetPrecision}
                toFixed
                suffix={targetUnit}
                css={{ textStyle: 'Text/L/Monospace/Regular', color: 'content.default.level-5' }}
              />
              {inputMode === 'qty' && (
                <div>
                  <Formatted.Number
                    value={
                      targetPrice && !Num(targetPrice).isZero()
                        ? Num(estTotalValue).multipliedBy(targetPrice).toString()
                        : '0'
                    }
                    precision={getQuoteCurrencyPrecision(instrument.quoteCurrency)}
                    toFixed
                    prefix='≈ '
                    suffix={instrument.quoteCurrency}
                    css={{ textStyle: 'Text/S/Monospace/Regular', color: 'content.default.level-2' }}
                  />
                </div>
              )}
            </dd>
          </div>
          <div>
            <dt>
              {position.marginType === 'Cross' ? (
                <Tooltip.Root placement='top-start'>
                  <Tooltip.Trigger asChild>
                    <span
                      className={css({
                        borderBottomWidth: 1,
                        borderBottomStyle: 'dashed',
                        borderBottomColor: 'border.default.level-3',
                      })}
                    >
                      <T id='Position.r250401.Est_liquidation_price' />
                    </span>
                  </Tooltip.Trigger>
                  <Tooltip.Content bottomSheetTitle={<T id='Position.r250401.Est_liquidation_price' />}>
                    <T id='Trade.r260506.Estimated_liquidation_price_desc' />
                  </Tooltip.Content>
                </Tooltip.Root>
              ) : (
                t('Position.r250401.Est_liquidation_price')
              )}
            </dt>
            <dd>
              <Formatted.Number
                value={estLiquidationPrice ?? '0'}
                precision={instrument.pricePrecision}
                toFixed
                suffix={instrument.quoteCurrency}
                zeroString='-'
              />
            </dd>
          </div>
        </dl>
      </MovableModal.Content>
    </>
  );
};

export default InputStep;
